/**
 * ehr#339 (Chase, 10-13): the Contacts Sort window, from his CRM sort-modal mockup
 * (CRM_10-13-26_1025AM-PDT_SORT_MODAL_CREDIFY_THEME_SUPPORT_1.html). Pure logic: the fields
 * it offers, the saved preference, the button's description, and a sort that keeps empty
 * values at the bottom whichever way it runs. The keys are the list's column keys, so the
 * window and a click on a column header drive the same sort.
 */

export type SortDir = 'asc' | 'desc';
export interface SortPref {
  /** A list column key; '' is the server's own order (last name, then first name). */
  key: string;
  dir: SortDir;
}

export interface SortField {
  key: string;
  label: string;
  asc: string;
  desc: string;
}

/** The window's choices, in the mockup's order. */
export const SORT_FIELDS: SortField[] = [
  { key: 'firstName', label: 'First name', asc: 'A to Z', desc: 'Z to A' },
  { key: 'name', label: 'Last name', asc: 'A to Z', desc: 'Z to A' },
  { key: 'clinician', label: 'Provider', asc: 'A to Z', desc: 'Z to A' },
  { key: 'intakeDate', label: 'Date added', asc: 'Oldest first', desc: 'Newest first' },
  { key: 'nextAction', label: 'Follow-up date', asc: 'Soonest first', desc: 'Latest first' },
];

/** The default, and what Reset to default goes back to: the server's last-name order. */
export const DEFAULT_SORT: SortPref = { key: '', dir: 'asc' };
export const SORT_LS_KEY = 'credify.contacts.sort';

export function normalizeSortPref(raw: unknown): SortPref {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SORT };
  const r = raw as { key?: unknown; dir?: unknown };
  return {
    key: typeof r.key === 'string' ? r.key : DEFAULT_SORT.key,
    dir: r.dir === 'desc' ? 'desc' : 'asc',
  };
}

/** The window's field for a sort: '' (the default order) is Last name, A to Z. */
export function sortFieldFor(key: string): SortField | undefined {
  return SORT_FIELDS.find((f) => f.key === (key || 'name'));
}

/**
 * "Last name: A to Z", for the Sort button. A column the window doesn't offer (Status,
 * Email…) reads with its column label, A to Z / Z to A.
 */
export function describeSort(pref: SortPref, columnLabel?: (key: string) => string): string {
  const f = sortFieldFor(pref.key);
  if (f) return `${f.label}: ${pref.dir === 'desc' ? f.desc : f.asc}`;
  const label = columnLabel?.(pref.key) ?? pref.key;
  return `${label}: ${pref.dir === 'desc' ? 'Z to A' : 'A to Z'}`;
}

/** A value to sort on; null or '' is empty and always goes to the bottom. */
export type SortValue = string | number | null;

/**
 * Sort `items` by `pref`. Empty values sit at the bottom either way; ties (and empties)
 * fall back to last name, then first name, as the mockup does.
 */
export function sortContactsBy<T extends { firstName: string; lastName: string }>(
  items: T[],
  pref: SortPref,
  valueOf: (key: string, item: T) => SortValue,
): T[] {
  if (!pref.key) return items;
  const sign = pref.dir === 'desc' ? -1 : 1;
  const blank = (v: SortValue) => v === null || (typeof v === 'string' && v.trim() === '');
  const text = (s: string) => s.trim().toLowerCase();
  const byName = (a: T, b: T) =>
    text(a.lastName).localeCompare(text(b.lastName)) ||
    text(a.firstName).localeCompare(text(b.firstName));
  return [...items].sort((a, b) => {
    const va = valueOf(pref.key, a);
    const vb = valueOf(pref.key, b);
    const ba = blank(va);
    const bb = blank(vb);
    if (ba !== bb) return ba ? 1 : -1;
    if (!ba) {
      const cmp =
        typeof va === 'string' && typeof vb === 'string'
          ? va.localeCompare(vb)
          : (va as number) < (vb as number)
            ? -1
            : (va as number) > (vb as number)
              ? 1
              : 0;
      if (cmp !== 0) return cmp * sign;
    }
    return byName(a, b);
  });
}
